import _ from "lodash";

// const Debug = require("debug");
// const $debug = Debug("ABI:warehouseUtils");

function missing(field = "a field") {
  throw new TypeError(`Missing ${field}`);
}

function invalid(field = "a field", msg = "is invalid") {
  throw new TypeError(`${field} ${msg}`);
}


const warehouseUtils = {
  /** Promise for refresh() */
  refresh_: null,
  /**
   * Only populated in static `warehouseUtils`, not per Vue instance.
   * Basically, its gonna be null if you access it like `this.warehousesWithInstancesComputed`
   */
  warehousesWithInstancesComputed: null,

	cachedWarehousesWithInstancesComputed() {
		return warehouseUtils.warehousesWithInstancesComputed
  },

  countryCodes: {
    AU: "Australia",
    NZ: "New Zealand",
    CN: "China",
    UK: "United Kingdom",
    // GB: "Great Britain", // this shouldn't be a thing
  },
  validSources: {
    mint: "Order originates from mint",
    bc: "Order originates from Business Central",
    augmented: "Order is a hard-coded mix of bc and/or mint",
    cypress: "Order originates from a Cypress test mock",
  },
  // From https://freedcamp.com/view/2713393/issuetracker/353352#comment-28564328
  warehouseTypes: {
    WH: "Warehouse",
    SH: "Showroom",
    DS: "Display Suite",
  },

  /** Takes $debuggingAssertions into account */
  assertIsWarehouse_(warehouse, options) {
    if (this.$debuggingAssertions) {
      this.assertIsWarehouse(warehouse, options);
    }
  },

  /** Doesn't take $debuggingAssertions into account */
  assertIsWarehouse(warehouse, { processed } = {}) {
    warehouse ?? missing("warehouse");
    warehouse._id ?? missing("warehouse._id");
    typeof warehouse._id === "string" || invalid('warehouse._id', "isn't a string (this may be because you haven't yet normalized mint numerical IDs)")
    typeof warehouse.title === "string" || missing("warehouse.title");
    typeof warehouse.titleShort === "string" || missing("warehouse.title");
    _.isArray(warehouse.source) || missing("warehouse.title");
    warehouse.source.forEach(
      (source) =>
        Object.keys(this.validSources).includes(source) ||
        invalid("warehouse.source", "contains invalid source")
    );
    typeof warehouse.permission === "string" || missing("warehouse.permission");
    typeof warehouse.country === "string" || missing("warehouse.country");
    Object.keys(this.countryCodes).includes(warehouse.country) ||
      // don't throw an error here, as adding new instances should only warn
      console.warn(
        warehouse.country + " is not a recognised country code, please add it to warehouseUtils.js countryCodes if that country code is intentional"
      );
    if (processed) {
      typeof warehouse.inInstanceWhitelist === "object" ||
        missing("warehouse.inInstanceWhitelist");
      Object.keys(this.countryCodes).forEach((code) => {
        typeof warehouse.inInstanceWhitelist[code] === "boolean" ||
          invalid(`warehouse.inInstanceWhitelist[${code}]`, "not a boolean");
      });
    }
  },

  /**
   * Returns a short string suitable for general display
   *
   * @returns {string} A short name
   */
  getWarehouseShortTitle(warehouse) {
    warehouseUtils.assertIsWarehouse_(warehouse);

    if (warehouse.title.length > 35) {
      return warehouse.titleShort;
    } else {
      return warehouse.title;
    }
  },

  /**
   * Doesn't display irrelevant warehouses according to business logic
   *
   * @param {Warehouse} warehouse Warehouse object
   * @param {Object} options Options object
   * @returns {boolean}
   */
  isInWhitelist(warehouse, options) {
    this.$debug("warehouseUtils.js isInWhitelist");
    this.assertIsWarehouse_(warehouse);
    if (!options.instance) missing("options.instance");

    const instanceWhitelist = this.$config.instances[
      options.instance
    ].warehouses.map((w) => w._id);

    const sameCountry = warehouse.country === options.instance;
    const whitelisted = instanceWhitelist.includes(warehouse._id);
    // const hasPermission = options.$session.hasPermission(`${warehouse.permission}View`)

    if (whitelisted && !sameCountry) {
      console.warn(
        `ERROR-instance-warehouse-whitelist-incoherent-country https://github.com/MomsFriendlyDevCo/ABI/blob/master/products/warehouseUtils.md#error-warehouse-whitelist-incoherent-country`
      );
    }

    return whitelisted;
  },

  /** Orders a list of warehouses according to business logic */
  orderWarehouses(warehouses) {
    this.$debug("warehouseUtils.js orderWarehouses", warehouses);
    if (!warehouses) missing("warehouse in warehouseUtils.orderWarehouses");
    warehouses.forEach((warehouse) => this.assertIsWarehouse_(warehouse));

    return warehouses.toSorted((a, b) => {
      a = a.source?.includes("bc") ?? false;
      b = b.source?.includes("bc") ?? false;
      if ((a && b) || (!a && !b)) {
        return 0;
      } else if (a && !b) {
        return -1;
      } else if (!a && b) {
        return 1;
      }
    });
  },
};


/**
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/this
 *
 * # Invariant
 * When resolved, data will have loaded and be ready for retrieval
 *
 * Recall that the `this` parameter of a standard function (warehouseUtils.refresh is a standard function)
 * is only determined by its callsite. When `Vue.instance('warehouseUtils') is called,
 * the vue instance will immediately call `this.refresh()`, such that inside the below defined function
 * `this.$debug` and `this.$digest` are well defined as expected inside a Vue instance.
 * To cache the processed warehouse data, the object `warehouseUtils` is used
 * as it only exists in one place in memory, opposite to `this` which refers to
 * abitrary memory as allocated by Vue.
 * `warehouseUtils` is accessible anywhere by `import warehouseUtils from ...`,
 * and only has one allocation when `warehouseUtils.js` is loaded by the browser.
 * This basically makes the `warehouseUtils` JS Object allocation a global singleton.
 *
 * @returns {Promise<void>}
 */
warehouseUtils.refresh = function () {
  this.$debug("warehouseUtils outer refresh called", warehouseUtils.refresh_);
  if (warehouseUtils.refresh_ instanceof Promise) return warehouseUtils.refresh_;

  warehouseUtils.refresh_ = Promise.resolve()
    .then(() => {
      this.$debug(
        "warehouseUtils inner cached refresh called (this should only be called once)"
      );
    })
    .then(() =>
      this.$digest.get("/api/ims/warehouses", {
        hash: "warehouses",
      })
    )
    .then((warehouses) => {
      if (!warehouses) {
        this.$toast.error("No warehouses returned by /api/ims/warehouses");
      }

      // Normalize mint numerical IDs to strings.
      // Shouldn't be necessary after thsi commit lands:
      // https://github.com/MomsFriendlyDevCo/ABI-IMS/commit/761628f450e3ac6ccbc0c52c3e5fac63c217b4b1
      warehouses.forEach((warehouse) => warehouse._id = warehouse._id.toString());

      // will always validate incoming data
      warehouses.forEach((warehouse) => this.assertIsWarehouse(warehouse));
      this.$debug("Starting to process warehouse data");
      const ordered = this.orderWarehouses(warehouses);
      const processedWarehouses = ordered.map((warehouse) => {
        warehouse.inInstanceWhitelist = {};
        Object.keys(this.countryCodes).forEach((instance) => {
          warehouse.inInstanceWhitelist[instance] = this.isInWhitelist(
            warehouse,
            { instance }
          );
        });
        return warehouse;
      });
      this.$debug("Processed warehouse data", processedWarehouses);
      processedWarehouses.forEach((warehouse) =>
        this.assertIsWarehouse_(warehouse, { processed: true })
      );
      return processedWarehouses;
    })
    .then((processedWarehouses) => {
      // don't save into `this` memory, save into `warehouseUtils.js` memory which exists in only one place
      warehouseUtils.warehousesWithInstancesComputed = processedWarehouses;
    });

  return warehouseUtils.refresh_;
},

module.exports = warehouseUtils;
